import api from '../httpClient';

export interface AIRecipeArchiveNutrition {
  [key: string]: unknown;
}

export type AIRecipeArchiveRecord = Record<string, unknown>;

export interface AIRecipeArchiveListItem {
  id: string;
  archiveKey?: string;
  reportDate: string;
  recipeName: string;
  description?: string | null;
  sourceRecipeId?: string | null;
  sourceDraftId?: string | null;
  draftName?: string | null;
  clientId?: string | null;
  nutritionistId?: string | null;
  draftGeneratedAt?: string | null;
  dayNumber?: number | null;
  dayDate?: string | null;
  mealOrder?: number | null;
  mealName?: string | null;
  mealType?: string | null;
  selected: boolean;
  ingredientCount: number;
  instructionCount: number;
  nutritionPerServing?: AIRecipeArchiveNutrition | null;
  cuisines?: string[];
  mealTypes?: string[];
  archivedAt?: string | null;
  createdAt?: string | null;
  reviewed: boolean;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  mappedIngredientCount: number;
}

export interface AIRecipeArchiveDocument extends Record<string, unknown> {
  id: string;
  reportDate: string;
  selected?: boolean;
  recipe?: AIRecipeArchiveRecord;
  ingredients?: AIRecipeArchiveRecord[];
  instructions?: AIRecipeArchiveRecord[];
  reviewed?: boolean;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  ingredientMappings?: AIRecipeIngredientMapping[];
}

export interface InternalIngredientMatch {
  id: string;
  name: string;
  displayName?: string | null;
  sourceId?: string | null;
  source?: string | null;
  possibleUnits?: string[];
  defaultServingId?: string | null;
  allergens?: string[];
  allergenTagIds?: string[];
  intoleranceTags?: string[];
  intoleranceTagIds?: string[];
  dietaryTagIds?: string[];
  isVegan?: boolean;
  isVegetarian?: boolean;
  isGlutenFree?: boolean;
  isDairyFree?: boolean;
}

export interface AIRecipeIngredientMapping {
  ingredientIndex: number | null;
  internalIngredientId: string;
  internalIngredientName?: string | null;
  quantity: number;
  unit: string;
  allergens?: string[];
  allergenTagIds?: string[];
  intoleranceTags?: string[];
  intoleranceTagIds?: string[];
}

export interface AIRecipeAdditionalIngredient {
  internalIngredientId: string;
  quantity: number;
  unit: string;
}

export interface AIRecipeReviewInput {
  ingredientMappings: AIRecipeIngredientMapping[];
  removedIngredientIndexes: number[];
  additionalIngredients: AIRecipeAdditionalIngredient[];
  instructions: AIRecipeInstructionEdit[];
  recipeUpdates: AIRecipeEditableFields;
}

export interface AIRecipeInstructionEdit {
  originalInstructionIndex: number | null;
  instruction: string;
}

export interface AIRecipeEditableFields {
  name: string;
  aliases: string[];
  description: string | null;
  cookingTips: string | null;
  servings: number;
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
  cuisines: string[];
  mealTypes: string[];
  dishTypes: string[];
  cookingMethod: string | null;
  dietLabels: string[];
  manualAllergens: string[];
  manualIntoleranceTags: string[];
  defaultServingId: string;
  defaultServingGrams: number;
  servingSizes: Array<{
    servingId: string;
    grams: number;
    ml?: number;
    densityGPerMl?: number;
    quantity?: number;
    label?: string;
    isDefault: boolean;
  }>;
}

export interface ListAIRecipeArchiveParams {
  page?: number;
  pageSize?: number;
  q?: string;
  reportDate?: string;
  selected?: 'all' | 'selected' | 'not_selected';
  reviewed?: 'all' | 'reviewed' | 'not_reviewed';
}

export interface AIRecipeArchivePagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export async function listAIRecipeArchive(params: ListAIRecipeArchiveParams = {}): Promise<{
  items: AIRecipeArchiveListItem[];
  pagination: AIRecipeArchivePagination;
}> {
  const response = await api.get('/recipes/admin/ai-archive', { params });
  return {
    items: Array.isArray(response.data?.data?.items) ? response.data.data.items : [],
    pagination: response.data?.data?.pagination ?? {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      totalCount: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };
}

export async function getAIRecipeArchive(id: string): Promise<AIRecipeArchiveDocument> {
  const response = await api.get(`/recipes/admin/ai-archive/${id}`);
  return response.data.data;
}

export async function deleteAIRecipeArchive(id: string): Promise<void> {
  await api.delete(`/recipes/admin/ai-archive/${id}`);
}

export async function searchInternalIngredientMatches(query: string): Promise<InternalIngredientMatch[]> {
  const response = await api.get('/ingredients/autocomplete', {
    params: { query, limit: 8, source: 'internal' },
  });
  const ingredients = response.data?.data?.ingredients;
  return Array.isArray(ingredients) ? ingredients : [];
}

export async function reviewAIRecipeArchive(
  id: string,
  review: AIRecipeReviewInput,
): Promise<void> {
  await api.put(`/recipes/admin/ai-archive/${id}/review`, review);
}
